import React from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  HStack,
  Icon,
  IconButton,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { LuCircleAlert, LuCircleCheck, LuLoader, LuCircle, LuX } from 'react-icons/lu';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

function getStepStatus(stepIndex, currentIndex, state) {
  if (state === 'done') return stepIndex <= currentIndex ? 'done' : 'todo';
  if (state === 'error') {
    if (stepIndex < currentIndex) return 'done';
    if (stepIndex === currentIndex) return 'error';
    return 'todo';
  }
  if (stepIndex < currentIndex) return 'done';
  if (stepIndex === currentIndex) return 'active';
  return 'todo';
}

function StepRow({ label, status }) {
  const iconProps = { boxSize: 4 };
  const colorMap = {
    done: 'green.400',
    active: 'blue.300',
    todo: 'gray.500',
    error: 'red.400',
  };
  const iconMap = {
    done: LuCircleCheck,
    active: LuLoader,
    todo: LuCircle,
    error: LuCircleAlert,
  };
  const icon = iconMap[status] || LuCircle;
  const color = colorMap[status] || 'gray.500';
  const isSpinning = status === 'active';
  return (
    <HStack spacing={3} align="center">
      <Icon
        as={icon}
        color={color}
        {...iconProps}
        sx={
          isSpinning
            ? {
                '@keyframes spin': {
                  from: { transform: 'rotate(0deg)' },
                  to: { transform: 'rotate(360deg)' },
                },
                animation: 'spin 1s linear infinite',
              }
            : undefined
        }
      />
      <Text fontSize="sm" color={status === 'todo' ? 'gray.500' : 'inherit'}>
        {label}
      </Text>
    </HStack>
  );
}

const SchedulingProgressCard = ({ progress, steps, onClose }) => {
  const bg = 'bg.surface';
  const border = 'border.subtle';
  const headerColor = 'text.primary';
  const successColor = useColorModeValue('green.600', 'green.300');
  const errorColor = useColorModeValue('red.600', 'red.300');
  const activeColor = useColorModeValue('blue.700', 'blue.200');
  const statusColor =
    progress.state === 'done'
      ? successColor
      : progress.state === 'error'
        ? errorColor
        : activeColor;

  return (
    <MotionBox
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 12 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
      borderWidth="1px"
      borderColor={border}
      bg={bg}
      borderRadius="lg"
      p={4}
      boxShadow="0 8px 24px rgba(0,0,0,0.28)"
      position="relative"
      overflow="hidden"
    >
      <Box
        position="absolute"
        insetX={0}
        top={0}
        height="3px"
        bg={statusColor}
        opacity={0.8}
      />
      <HStack justify="space-between" align="flex-start" mb={2}>
        <Box>
          <Text fontWeight="semibold" color={headerColor}>
            Planning it out
          </Text>
        </Box>
        {onClose && (
          <IconButton
            aria-label="Dismiss status"
            icon={<LuX />}
            size="sm"
            variant="ghost"
            onClick={onClose}
          />
        )}
      </HStack>

      <Box
        mt={2}
        px={3}
        py={2}
        borderRadius="md"
        bg={useColorModeValue('gray.50', 'rgba(255,255,255,0.04)')}
        borderWidth="1px"
        borderColor={useColorModeValue('rgba(0,0,0,0.08)', 'rgba(255,255,255,0.12)')}
      >
        <Text fontSize="sm" color={headerColor} fontWeight="medium">
          “{progress.text}”
        </Text>
      </Box>

      <VStack align="stretch" spacing={2} mt={3}>
        {steps.map((step, idx) => (
          <StepRow
            key={step.key}
            label={step.label}
            status={getStepStatus(idx, progress.stepIndex, progress.state)}
          />
        ))}
      </VStack>

      {progress.state === 'error' && (
        <Alert status="error" mt={3} borderRadius="md">
          <AlertIcon />
          <AlertDescription fontSize="sm">
            {progress.errorMessage || 'We could not save this right now. Please try again.'}
          </AlertDescription>
        </Alert>
      )}
    </MotionBox>
  );
};

export default SchedulingProgressCard;
